// Retell AI Voice Hotline Route
import { useEffect, useState } from 'react'

import { VoiceInterface } from '#app/components/hotline/voice-interface.tsx'
import { type Route } from '#app/routes/hotline+/+types/retell.ts'
import { createVoiceProvider } from '#app/utils/voice-providers/factory.ts'
import { type IVoiceAgentProvider } from '#app/utils/voice-providers/types.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Voice Hotline (Retell AI) - Lydra AI' },
]

export default function RetellHotlineRoute() {
	const [provider, setProvider] = useState<IVoiceAgentProvider | null>(null)
	const [isProviderReady, setIsProviderReady] = useState(false)
	const [providerError, setProviderError] = useState<string>()

	// Initialize Retell provider
	useEffect(() => {
		const initializeProvider = async () => {
			try {
				const retellProvider = createVoiceProvider('retell')

				// Check if provider is supported
				const isSupported = await retellProvider.isSupported()
				if (!isSupported) {
					throw new Error(
						'Retell AI is not supported in this browser. Please use a modern browser with WebRTC support.',
					)
				}

				// Check if agent ID is configured
				const agentId = globalThis.ENV?.RETELL_AGENT_ID
				if (!agentId) {
					throw new Error(
						'Retell Agent ID not configured. Please set RETELL_AGENT_ID in your environment.',
					)
				}

				setProvider(retellProvider)
				setIsProviderReady(true)
			} catch (error) {
				const errorMessage =
					error instanceof Error
						? error.message
						: 'Failed to initialize Retell provider'
				setProviderError(errorMessage)
				console.error('Failed to initialize Retell provider:', error)
			}
		}

		void initializeProvider()

		// Cleanup on unmount
		return () => {
			if (provider) {
				provider.destroy()
			}
		}
	}, [provider])

	// Show error state if provider failed to initialize
	if (providerError) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
				<div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
					<h2 className="mb-3 text-lg font-semibold text-red-900">
						Service Unavailable
					</h2>
					<p className="text-sm text-red-700">{providerError}</p>
					<p className="text-muted-foreground mt-3 text-xs">
						Please contact support if this issue persists.
					</p>
				</div>
			</div>
		)
	}

	// Show loading state while provider initializes
	if (!isProviderReady || !provider) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
				<div className="text-center">
					<div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-600" />
					<p className="text-muted-foreground text-sm">
						Initializing Retell AI voice service...
					</p>
				</div>
			</div>
		)
	}

	// Render voice interface with Retell provider
	return (
		<VoiceInterface
			provider={provider}
			providerConfig={{
				debug: globalThis.ENV?.MODE === 'development',
				agentId: globalThis.ENV?.RETELL_AGENT_ID,
				sampleRate: 24000,
			}}
			title="Voice Hotline"
			description="Powered by Retell AI"
		/>
	)
}
